import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Envelope, User, Phone, PaperPlaneTilt, CircleNotch, CheckCircle, XCircle } from '@phosphor-icons/react'
import { userApi } from '@/api/users'
import type { ProvisionUserRequest } from '@/api/users'
import { useToast } from '@/hooks/use-toast'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface UserProvisionSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function UserProvisionSheet({ open, onOpenChange, orgId }: UserProvisionSheetProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Form state
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [roleSlug, setRoleSlug] = useState('')
  const [phone, setPhone] = useState('')
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)

  // Email validation state
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailError, setEmailError] = useState('')

  // Fetch roles for the org
  const { data: roles } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: () => userApi.listRoles(orgId),
    enabled: open && !!orgId,
  })

  // Debounced email uniqueness check
  useEffect(() => {
    if (!email || !isValidEmail(email)) {
      setEmailAvailable(null)
      setEmailError('')
      return
    }
    setEmailChecking(true)
    const timer = setTimeout(async () => {
      try {
        const result = await userApi.checkEmail(email)
        setEmailAvailable(result.available)
        setEmailError(result.available ? '' : 'Email already in use')
      } catch {
        setEmailAvailable(null)
        setEmailError('')
      }
      setEmailChecking(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [email])

  // Reset form when sheet opens/closes
  useEffect(() => {
    if (!open) {
      setEmail('')
      setName('')
      setRoleSlug('')
      setPhone('')
      setSendWelcomeEmail(true)
      setEmailAvailable(null)
      setEmailChecking(false)
      setEmailError('')
    }
  }, [open])

  const provisionMutation = useMutation({
    mutationFn: (data: ProvisionUserRequest) => userApi.provision(orgId, data),
    onSuccess: () => {
      toast({
        title: 'User provisioned successfully',
        description: `${name} has been added to the organization.`,
      })
      queryClient.invalidateQueries({ queryKey: ['users', orgId] })
      queryClient.invalidateQueries({ queryKey: ['organizations', 'detail', orgId] })
      onOpenChange(false)
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 409 || error.message?.toLowerCase().includes('already')) {
        setEmailError('Email already in use')
        setEmailAvailable(false)
      } else {
        toast({
          title: 'Failed to provision user',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!email || !name || !roleSlug) return
    if (emailAvailable === false) return

    const data: ProvisionUserRequest = {
      email: email.trim(),
      name: name.trim(),
      role_slug: roleSlug,
      ...(phone.trim() && { phone: phone.trim() }),
      send_welcome_email: sendWelcomeEmail,
    }

    provisionMutation.mutate(data)
  }

  const canSubmit =
    email.trim() !== '' &&
    isValidEmail(email) &&
    name.trim() !== '' &&
    roleSlug !== '' &&
    emailAvailable !== false &&
    !emailChecking &&
    !provisionMutation.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Add User</SheetTitle>
          <SheetDescription>
            Provision a new user for this organization. They will receive login
            credentials via email.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-6">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="provision-email" className="flex items-center gap-1.5">
              <Envelope className="h-4 w-4 text-muted-foreground" />
              Email <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="provision-email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={
                  emailError
                    ? 'border-destructive pr-9'
                    : emailAvailable === true
                      ? 'border-emerald-500 pr-9'
                      : 'pr-9'
                }
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {emailChecking && (
                  <CircleNotch className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
                {!emailChecking && emailAvailable === true && (
                  <CheckCircle className="h-4 w-4 text-emerald-500" weight="fill" />
                )}
                {!emailChecking && emailAvailable === false && (
                  <XCircle className="h-4 w-4 text-destructive" weight="fill" />
                )}
              </div>
            </div>
            {emailAvailable === true && (
              <p className="text-xs text-emerald-600">Available</p>
            )}
            {emailError && (
              <p className="text-xs text-destructive">{emailError}</p>
            )}
          </div>

          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="provision-name" className="flex items-center gap-1.5">
              <User className="h-4 w-4 text-muted-foreground" />
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="provision-name"
              type="text"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              Role <span className="text-destructive">*</span>
            </Label>
            <Select value={roleSlug} onValueChange={setRoleSlug}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles?.map((role) => (
                  <SelectItem key={role.slug} value={role.slug}>
                    {role.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="provision-phone" className="flex items-center gap-1.5">
              <Phone className="h-4 w-4 text-muted-foreground" />
              Phone <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="provision-phone"
              type="tel"
              placeholder="+91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {/* Welcome email toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={sendWelcomeEmail}
              onClick={() => setSendWelcomeEmail(!sendWelcomeEmail)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                sendWelcomeEmail ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                  sendWelcomeEmail ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
            <Label className="flex items-center gap-1.5 cursor-pointer" onClick={() => setSendWelcomeEmail(!sendWelcomeEmail)}>
              <PaperPlaneTilt className="h-4 w-4 text-muted-foreground" />
              Send welcome email
            </Label>
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={provisionMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {provisionMutation.isPending && (
                <CircleNotch className="h-4 w-4 animate-spin mr-1.5" />
              )}
              Add User
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
